import { useState } from "react";
import { useTranslation } from "react-i18next";
import { parseBarcode, type TransferDto, type TransferSource, vnDate } from "@hisense/shared";
import { useReceiptWarehouses } from "@/api/warehouseReceipts";
import { ApiClientError, apiFetch } from "@/lib/api";
import { formatQty } from "@/domain/money";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { useStockMoveAccess } from "@/components/warehouse/StockMoveRules";
import { ScanField, cleanQuantity, locationFromScan, lotScan, sameProduct, scanError } from "./ScanField";

const today = () => vnDate();

/**
 * Cất / chuyển: quét tem lô → chọn vị trí đang chứa (nếu lô ở nhiều chỗ) → quét / gõ vị trí mới → Chuyển. Cùng kho thì
 * chốt ngay một bước (cất lên kệ, đổi kệ); khác kho thì gửi đi, kho nhận xác nhận ở trang Chuyển lô. Gửi và nhận là ô
 * SỬA ở Biến động kho (chốt 20/09/2026 — chuyển hàng là việc của kho, đổi số tồn mới cần Duyệt); không có thì phiếu nằm
 * nháp và màn này nói rõ.
 */
export function MoveTask() {
  const { t } = useTranslation();
  const canFinish = useStockMoveAccess()("update");
  const warehouses = useReceiptWarehouses();
  const [positions, setPositions] = useState<TransferSource[]>([]);
  const [picked, setPicked] = useState<TransferSource | null>(null);
  const [toWarehouse, setToWarehouse] = useState("");
  const [toLocation, setToLocation] = useState("");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState(() => t("scan.move.reasonDefault"));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const choose = (p: TransferSource) => {
    setPicked(p);
    setToWarehouse(p.warehouseId);
    setQuantity(p.availableQuantity);
    // Cất lên kệ: điền sẵn Kệ mặc định của sản phẩm (nếu lô chưa nằm ở đó); người cất sửa / quét đè được.
    setToLocation(p.defaultLocation && p.defaultLocation !== p.location ? p.defaultLocation : "");
  };
  const scan = async (raw: string) => {
    setError(null);
    setNotice(null);
    setPicked(null);
    setPositions([]);
    const hit = lotScan(raw, parseBarcode);
    if (!hit) return setError(t("scan.parseError", { code: raw }));
    setBusy(true);
    try {
      const res = await apiFetch<{ data: TransferSource[] }>("/lot-transfers/sources", { query: { q: hit.lotNumber } });
      const exact = res.data.filter((s) => s.lotNumber.trim() === hit.lotNumber && sameProduct(hit.productCode, s.productCode));
      setPositions(exact);
      if (!exact.length) setError(t("scan.move.noAvailable"));
      else if (exact.length === 1) choose(exact[0]!);
    } catch (e) {
      setError(scanError(e, t, raw));
    } finally {
      setBusy(false);
    }
  };
  const warehouseName = (id: string) => warehouses.data?.data.find((w) => w.id === id)?.name ?? id;
  const submit = async () => {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    let created: TransferDto | null = null;
    try {
      const res = await apiFetch<{ data: TransferDto }>("/lot-transfers", {
        method: "POST",
        body: { requestId: crypto.randomUUID(), entryId: picked.entryId, fromWarehouseId: picked.warehouseId, fromLocation: picked.location,
          toWarehouseId: toWarehouse, toLocation: locationFromScan(toLocation), quantity: quantity.trim(), movedDate: today(), reason: reason.trim(), notes: "" },
      });
      created = res.data;
      const sameWarehouse = toWarehouse === picked.warehouseId;
      // Chốt chuyển trong một kho cần quyền Duyệt: không có thì dừng ở phiếu nháp (khỏi gọi rồi nhận lỗi quyền).
      if (sameWarehouse && !canFinish) {
        setError(t("scan.move.draftOnly", { no: created.transferNo }));
        setPicked(null);
        setPositions([]);
        setToLocation("");
        return;
      }
      await apiFetch(`/lot-transfers/${created.id}/${sameWarehouse ? "receive" : "ship"}`, { method: "POST", body: { revision: created.revision } });
      setNotice(sameWarehouse
        ? t("scan.move.done", { qty: formatQty(Number(quantity)), unit: picked.unit, from: `${picked.warehouseName} ${picked.location}`, to: `${warehouseName(toWarehouse)} ${locationFromScan(toLocation)}`, no: created.transferNo })
        : t("scan.move.shipped", { qty: formatQty(Number(quantity)), unit: picked.unit, warehouse: warehouseName(toWarehouse), no: created.transferNo }));
      setPicked(null);
      setPositions([]);
      setToLocation("");
    } catch (e) {
      if (created && e instanceof ApiClientError && e.status === 403) {
        // Phiếu nháp đã lập: xoá màn để bấm lại không lập thêm một phiếu nháp nữa.
        setError(t("scan.move.draftOnly", { no: created.transferNo }));
        setPicked(null);
        setPositions([]);
      } else setError(scanError(e, t, ""));
    } finally {
      setBusy(false);
    }
  };
  const ready = !!picked && !!toWarehouse && !!locationFromScan(toLocation) && reason.trim().length >= 5 && cleanQuantity(quantity) && Number(quantity) > 0
    && Number(quantity) <= Number(picked.availableQuantity) && !(toWarehouse === picked.warehouseId && locationFromScan(toLocation) === picked.location);
  return (
    <div className="space-y-4">
      <ScanField id="scan-move-lot" label={t("scan.scanLot")} placeholder={t("scan.scanLotPlaceholder")} busy={busy} autoFocus={!picked} onScan={(v) => void scan(v)} />
      {error && <p role="alert" className="text-sm text-danger">{error}</p>}
      {notice && <p role="status" className="rounded-sm bg-ok-tint px-3 py-2 text-sm text-ok">{notice}</p>}
      {positions.length > 1 && (
        <fieldset className="min-w-0 space-y-2">
          <legend className="text-sm text-ink-soft">{t("scan.move.choose")}</legend>
          {positions.map((p) => (
            <label key={`${p.entryId}|${p.warehouseId}|${p.location}`} className="flex min-h-12 items-center gap-3 rounded-sm border border-line bg-surface px-3 text-sm">
              <input type="radio" name="scan-move-from" checked={picked === p} onChange={() => choose(p)} />
              <span>{p.warehouseName} · <span className="font-mono">{p.location}</span> · <span className="font-mono tabular">{formatQty(Number(p.availableQuantity))}</span> {p.unit}</span>
            </label>
          ))}
        </fieldset>
      )}
      {picked && (
        <div className="space-y-3 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-base font-semibold">{picked.productCode}{picked.subRef && <span className="text-ink-soft"> / {picked.subRef}</span>}</p>
          <p className="font-mono text-sm">{picked.lotNumber}</p>
          <p className="text-sm">{t("scan.move.from")}: {picked.warehouseName} · <span className="font-mono">{picked.location}</span> · <span className="font-mono tabular">{formatQty(Number(picked.availableQuantity))}</span> {picked.unit}</p>
          <div>
            <FieldLabel htmlFor="scan-move-wh">{t("scan.move.toWarehouse")}</FieldLabel>
            <Select id="scan-move-wh" containerClassName="w-full" value={toWarehouse} onChange={(e) => setToWarehouse(e.target.value)}>
              {warehouses.data?.data.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.code})</option>)}
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="scan-move-loc">{t("scan.move.toLocation")}</FieldLabel>
            <Input id="scan-move-loc" className="h-12 font-mono text-base" autoComplete="off" autoCapitalize="characters" value={toLocation}
              onChange={(e) => setToLocation(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setToLocation(locationFromScan(toLocation)); } }} autoFocus />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel htmlFor="scan-move-qty">{t("scan.move.quantity")}</FieldLabel>
              <Input id="scan-move-qty" className="h-12 font-mono text-base" inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
            <div>
              <FieldLabel htmlFor="scan-move-reason">{t("scan.move.reason")}</FieldLabel>
              <Input id="scan-move-reason" className="h-12" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </div>
          <Button className="h-12 w-full" disabled={!ready || busy} onClick={() => void submit()}>{t("scan.move.submit")}</Button>
        </div>
      )}
    </div>
  );
}
